import { useEffect, useRef } from 'react';
import { FirebaseError } from 'firebase/app';
import { useAuthStore } from '../store/auth-store';
import { queryClient } from '../lib/query-client';
import { userKeys } from './use-user-info';
import Logger from '../utils/logger';
import {
  signInWithGoogle,
  signInWithMicrosoft,
  signInWithYahoo,
  signOut,
  onAuthStateChange,
  sendEmailSignInLink,
  isEmailSignInUrl,
  completeEmailSignIn,
  createRecaptchaVerifier,
  sendPhoneVerificationCode,
  confirmPhoneCode,
} from '../services/auth';
import type { User, RecaptchaVerifier } from 'firebase/auth';

type SignInFn = () => Promise<User>;

const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/popup-blocked': 'O popup foi bloqueado pelo navegador. Permita popups e tente novamente.',
  'auth/popup-closed-by-user': 'O login foi cancelado.',
  'auth/cancelled-popup-request': 'O login foi cancelado.',
  'auth/network-request-failed': 'Erro de conexão. Verifique sua internet e tente novamente.',
  'auth/user-disabled': 'Esta conta foi desativada.',
  'auth/account-exists-with-different-credential': 'Já existe uma conta com este e-mail usando outro método de login.',
  'auth/operation-not-allowed': 'Este método de login não está habilitado.',
  'auth/invalid-email': 'E-mail inválido.',
  'auth/invalid-phone-number': 'Número de telefone inválido.',
  'auth/invalid-verification-code': 'Código de verificação inválido.',
  'auth/code-expired': 'O código expirou. Solicite um novo.',
  'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
  'auth/missing-phone-number': 'Informe o número de telefone.',
};

export function useAuth() {
  const {
    user,
    loading,
    error,
    emailForSignIn,
    phoneConfirmationResult,
    setUser,
    setLoading,
    setError,
    setEmailForSignIn,
    setPhoneConfirmationResult,
    clearPhoneConfirmation,
  } = useAuthStore();

  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);

      if (!user) {
        queryClient.removeQueries({ queryKey: userKeys.me });
      }
    });

    return () => unsubscribe();
  }, [setUser]);

  const handleFirebaseError = (err: unknown, fallbackMessage: string) => {
    const isKnown = err instanceof FirebaseError && err.code in AUTH_ERROR_MESSAGES;

    if (err instanceof FirebaseError && err.code in AUTH_ERROR_MESSAGES) {
      setError(AUTH_ERROR_MESSAGES[err.code]);
    } else {
      setError(err instanceof Error ? err.message : fallbackMessage);
    }

    Logger.error('Firebase auth error', {
      error_code: err instanceof FirebaseError ? err.code : undefined,
      error_message: err instanceof Error ? err.message : fallbackMessage,
      is_known_error: isKnown,
    });
  };

  const handleSignIn = async (signInFn: SignInFn) => {
    try {
      setError(null);
      setLoading(true);
      await signInFn();
    } catch (err) {
      handleFirebaseError(err, 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setError(null);
      setLoading(true);
      queryClient.removeQueries({ queryKey: userKeys.me });
      await signOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer logout');
    } finally {
      setLoading(false);
    }
  };

  // Email Link

  const handleSendEmailLink = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      await sendEmailSignInLink(email);
      localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, JSON.stringify({ email, timestamp: Date.now() }));
      setEmailForSignIn(email);
    } catch (err) {
      handleFirebaseError(err, 'Erro ao enviar link de login.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteEmailSignIn = async (manualEmail?: string) => {
    const href = window.location.href;
    if (!isEmailSignInUrl(href)) {
      return;
    }

    let email = manualEmail;

    if (!email) {
      const stored = localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as { email: string; timestamp: number };
          const THIRTY_MINUTES = 30 * 60 * 1000;
          if (Date.now() - parsed.timestamp > THIRTY_MINUTES) {
            localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
          } else {
            email = parsed.email;
          }
        } catch {
          localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
        }
      }
    }

    if (!email) {
      // If we still don't have an email, we need the user to provide it
      return { needsEmail: true };
    }

    try {
      setError(null);
      setLoading(true);
      await completeEmailSignIn(email, href);
      localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
      setEmailForSignIn(null);
    } catch (err) {
      handleFirebaseError(err, 'Erro ao completar login por e-mail.');
    } finally {
      setLoading(false);
    }
  };

  // Phone

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    return () => {
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
    };
  }, []);

  const getRecaptchaVerifier = () => {
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = createRecaptchaVerifier('recaptcha-container');
    }
    return recaptchaVerifierRef.current;
  };

  const handleSendPhoneCode = async (phoneNumber: string) => {
    if (loading) {
      return;
    }
    try {
      setError(null);
      const verifier = getRecaptchaVerifier();
      setLoading(true);
      const confirmationResult = await sendPhoneVerificationCode(phoneNumber, verifier);
      setPhoneConfirmationResult(confirmationResult);
    } catch (err) {
      handleFirebaseError(err, 'Erro ao enviar código SMS.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPhoneCode = async (code: string) => {
    if (!phoneConfirmationResult) {
      setError('Nenhum código pendente. Solicite um novo código.');
      return;
    }

    try {
      setError(null);
      setLoading(true);
      await confirmPhoneCode(phoneConfirmationResult, code);
      clearPhoneConfirmation();
    } catch (err) {
      handleFirebaseError(err, 'Erro ao verificar código.');
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    emailForSignIn,
    phoneConfirmationResult,
    signInWithGoogle: () => handleSignIn(signInWithGoogle),
    signInWithMicrosoft: () => handleSignIn(signInWithMicrosoft),
    signInWithYahoo: () => handleSignIn(signInWithYahoo),
    signOut: handleSignOut,
    handleSendEmailLink,
    handleCompleteEmailSignIn,
    resetEmailSignIn: () => {
      setEmailForSignIn(null);
      localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
    },
    handleSendPhoneCode,
    handleConfirmPhoneCode,
    resetPhoneSignIn: () => {
      clearPhoneConfirmation();
    },
  };
}
